import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Divider, IconButton, Tooltip, useTheme } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  Strategy,
  Leg,
  MarketData,
  OptionTabMetaData,
  isString,
  isArray,
  PositionData,
  PositionWithExpiryData,
} from '../ChartSidebar/types';
import moment from 'moment';
import useGetOptionChainMetadata from 'services/Simulator/useGetOptionChainMetadata';
import useGetOptionChainData from 'services/Simulator/useGetOptionChianData';
import { TVChartContainer } from '../index';
import ChartSidebar from '../ChartSidebar/index';
import { updateGlobalPositions } from '../Datafeeds/datafeed';
import { convertPositionsToApiFormat } from '../ChartSidebar/legsUtils';
import { Helmet } from 'react-helmet-async';
import PageHeader from 'app/design/speedBot/PageHeader';
import MuiChip from 'app/design/uiComponents/MuiChip';

// Styled components
const Container = styled(Box)(() => ({
  display: 'flex',
  height: '100vh',
  width: '100%',
}));

const SidebarWrapper = styled(Box)<{ isOpen: boolean }>(({ isOpen }) => ({
  width: isOpen ? '600px' : '0px',
  height: '100%',
  overflow: 'hidden',
  transition: 'width 0.3s ease-in-out',
  borderRight: '1px solid #e0e0e0',
  backgroundColor: '#fafafa',
}));

const ChartWrapper = styled(Box)(() => ({
  flex: 1,
  height: '100%',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
}));

const ToggleButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: '10px',
  left: '10px',
  zIndex: 1000,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
}));

// Mock data
const mockMarketData: MarketData[] = [
  {
    symbol: 'NIFTY',
    value: 24832.2,
    change: -219.7,
    changePercent: -0.88,
  },
];

const getDefaultDate = () => {
  const today = moment();
  let prev = today.clone().subtract(1, 'days');
  // If previous day is Sunday (0) or Saturday (6), go back to Friday
  if (prev.day() === 0) {
    // Sunday
    prev = prev.subtract(2, 'days');
  } else if (prev.day() === 6) {
    // Saturday
    prev = prev.subtract(1, 'days');
  }
  return prev.format('YYYY-MM-DD 15:29:00');
};

const EnhancedTVChartContainer: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showUnderlying, setShowUnderlying] = useState(false);
  const [legs, setLegs] = useState<Leg[]>([]); // Initialize with empty array
  const [marketData, setMarketData] = useState<MarketData[]>(mockMarketData);

  const [positionUpdateFlag, setPositionUpdateFlag] = useState(false);

  // Positions state for persistence between Option Tab and ChartSidebar
  const [positions, setPositions] = useState<PositionWithExpiryData>({});

  // API related states
  const [date] = useState<string>(getDefaultDate());
  const [optionTabMetaData, setOptionTabMetaData] = useState<OptionTabMetaData>(
    {
      symbol: 'NIFTY 50',
      optionExpiry: '',
      future: '',
    },
  );

  // API calls
  const { data: queryData } = useGetOptionChainMetadata(
    moment.utc(date).format('YYYY-MM-DD').toString(),
  );

  const metaData = queryData?.data;

  // Use useMemo to prevent unnecessary re-renders with safe type checking
  const indexes = useMemo(() => {
    if (
      metaData &&
      metaData.lot_sizes &&
      typeof metaData.lot_sizes === 'object'
    ) {
      const keys = Object.keys(metaData.lot_sizes);
      return keys.filter(key => isString(key));
    }
    return ['NIFTY 50'];
  }, [metaData]);

  // Get available expiry dates for current symbol
  const optionExpiry = useMemo(() => {
    if (metaData && optionTabMetaData.symbol) {
      return metaData.options && metaData.options[optionTabMetaData.symbol];
    }
    return [];
  }, [metaData, optionTabMetaData.symbol]);

  // Update optionTabMetaData when metadata changes
  useEffect(() => {
    if (metaData && indexes.length > 0) {
      const currentSymbol = optionTabMetaData.symbol || indexes[0];

      // Safe access to options and futures with proper type checking
      const symbolOptions = metaData.options && metaData.options[currentSymbol];
      const symbolFutures = metaData.futures && metaData.futures[currentSymbol];

      setOptionTabMetaData(prev => ({
        symbol: prev.symbol || indexes[0],
        optionExpiry:
          isArray(symbolOptions) &&
          symbolOptions[0] &&
          isString(symbolOptions[0])
            ? symbolOptions[0]
            : '',
        future:
          isArray(symbolFutures) &&
          symbolFutures[0] &&
          isString(symbolFutures[0])
            ? symbolFutures[0]
            : '',
      }));
    }
  }, [metaData, indexes, optionTabMetaData.symbol]);

  // Update optionTabMetaData when symbol changes
  useEffect(() => {
    if (metaData && optionTabMetaData.symbol) {
      const symbolOptions =
        metaData.options && metaData.options[optionTabMetaData.symbol];

      if (isArray(symbolOptions) && symbolOptions.length > 0) {
        setOptionTabMetaData(prev => ({
          ...prev,
          optionExpiry: symbolOptions[0],
        }));
      }
    }
  }, [metaData, optionTabMetaData.symbol]);

  const { data: optionChainStrikeData } = useGetOptionChainData(
    optionTabMetaData.symbol,
    optionTabMetaData.optionExpiry,
    moment(date, 'YYYYMMDD HH:mm')
      .utcOffset(330)
      .utc()
      .format('YYYY-MM-DD HH:mm:ss')
      .toString(),
  );

  // Update market data when option chain data changes
  useEffect(() => {
    if (
      optionChainStrikeData &&
      typeof optionChainStrikeData.spot_price === 'number'
    ) {
      setMarketData(prev =>
        prev.map(item =>
          item.symbol === optionTabMetaData.symbol
            ? { ...item, value: optionChainStrikeData.spot_price }
            : item,
        ),
      );
    }
  }, [optionChainStrikeData, optionTabMetaData.symbol]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleStrategySelect = () => {
    // TODO: Implement strategy selection logic
    // This could involve:
    // 1. Adding the strategy legs to the legs array
    // 2. Updating the chart with the strategy
    // 3. Showing payoff diagrams
  };

  const handleLegAdd = useCallback((leg?: Leg) => {
    if (leg) {
      // Add the leg with a unique ID if not provided
      const legWithId = {
        ...leg,
        id: leg.id || `${leg.contractType}_${leg.strike}_${Date.now()}`,
      };
      setLegs(prevLegs => [...prevLegs, legWithId]);
    } else {
      // Add a default leg (for testing)
      const defaultLeg: Leg = {
        id: `default_${Date.now()}`,
        direction: 'long',
        expiry: '2025-07-31',
        strike: '25000',
        lots: 1,
        ltp: 100,
        entryTime: new Date().toISOString(),
        contractType: 'CE',
        entryPrice: 100,
        isActive: true,
        iv: 0.2,
        premium: 100,
        contractDetails: {},
      };
      setLegs(prevLegs => [...prevLegs, defaultLeg]);
    }
  }, []);

  const handleLegEdit = useCallback((updatedLeg: Leg) => {
    setLegs(prevLegs =>
      prevLegs.map(leg => (leg.id === updatedLeg.id ? updatedLeg : leg)),
    );
  }, []);

  const handleLegDelete = useCallback((legId: string) => {
    if (legId === 'clear-all') {
      setLegs([]);
    } else {
      setLegs(prevLegs => prevLegs.filter(leg => leg.id !== legId));
    }
  }, []);

  const handleClearLegs = useCallback(() => {
    setLegs([]);
  }, []);

  const handleShowUnderlyingChange = useCallback((show: boolean) => {
    setShowUnderlying(show);
    // TODO: Update chart to show/hide underlying asset
  }, []);

  const handleSymbolChange = useCallback((symbol: string) => {
    setOptionTabMetaData(prev => ({
      ...prev,
      symbol: symbol,
    }));
  }, []);

  const handlePositionUpdate = useCallback(
    (updatedPositions: PositionWithExpiryData) => {
      setPositions(updatedPositions);

      // Use the utility function to convert positions to API format
      const apiFormatObject = convertPositionsToApiFormat(
        updatedPositions,
        optionTabMetaData.symbol,
      );

      updateGlobalPositions(apiFormatObject.positions);
    },
    [optionTabMetaData.symbol],
  );

  // NEW: Handler for position flag trigger
  const handlePositionFlagTrigger = useCallback(() => {
    setPositionUpdateFlag(true);

    // Reset flag after delay
    setTimeout(() => {
      setPositionUpdateFlag(false);
    }, 1000);
  }, []);

  const theme = useTheme();

  return (
    <Box>
      <Helmet>
        <title>Charts </title>
        <meta name="description" content="Charts Page" />
      </Helmet>
      <Box className="sticky-top" sx={{
        backgroundColor: theme.palette.common.white,
        transition: theme.transitions.create(['transform', 'opacity'], {
          duration: theme.transitions.duration.standard,
        }),
      }}>

        <PageHeader
          variant="market"
          title={
            <>
              Charts
              <MuiChip
                label={'Beta'}
                size="medium"
                color={'greylight'}
                sx={{ m: 1 }}
              />
            </>
          }
        />
        <Divider sx={{ borderBottom: 2, borderColor: 'grey.300' }} />
      </Box>
      <Container sx={{ height: 'calc(100vh - 90px)' }}>
        {/* Sidebar */}
        <SidebarWrapper isOpen={sidebarOpen}>
          <ChartSidebar
            marketData={marketData}
            legs={legs}
            showUnderlying={showUnderlying}
            onLegAdd={handleLegAdd}
            onLegEdit={handleLegEdit}
            onLegDelete={handleLegDelete}
            onClearLegs={handleClearLegs}
            onShowUnderlyingChange={handleShowUnderlyingChange}
            // New props for symbol selection
            optionTabMetaData={optionTabMetaData}
            setOptionTabMetaData={setOptionTabMetaData}
            optionChainData={optionChainStrikeData}
            optionChainMetadata={metaData}
            indexes={indexes}
            onSymbolChange={handleSymbolChange}
            lotSize={1}
            // Position persistence props
            positions={positions}
            onPositionUpdate={handlePositionUpdate}
            onPositionFlagTrigger={handlePositionFlagTrigger}
          />
        </SidebarWrapper>

        {/* Chart Area */}
        <ChartWrapper>
          {/* Toggle Button */}
          <ToggleButton onClick={handleSidebarToggle}>
            <Tooltip title={sidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}>
              {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </Tooltip>
          </ToggleButton>

          {/* TradingView Chart */}
          <TVChartContainer
            symbol="NSE:NIFTY 50"
            interval="15"
            containerId="tv_chart_container"
            libraryPath="/charting-library/charting_library/"
            fullscreen={false}
            autosize={true}
            positionUpdateFlag={positionUpdateFlag}
          />
        </ChartWrapper>
      </Container>
    </Box>

  );
};

export default EnhancedTVChartContainer;
